<template>
  <EmptyState
    v-if="!loading && users.length === 0"
    @onEmptyStateButtonClick="$router.push({ name: 'newUserAccount' })"
    message="Ainda não tem utilizadores criados"
    buttonText="Criar utilizador"
  ></EmptyState>

  <div class="header-content">
    <div class="flex mt-2 justify-content-between align-items-center">
      <div
        class="inline-flex align-items-center results"
        v-if="users.length"
        style="font-size: 14px; color: grey"
      >
        <span>{{ users.length }} resultado(s)</span>
      </div>
      <div class="inline-flex flex-auto"></div>
      <div class="inline-flex">
        <Button
          label="Criar utilizador"
          v-if="auth.account_state == 'ENABLED'"
          class="p-button p-button-secondary"
          @click="$router.push({ name: 'newUserAccount' })"
        />
      </div>
    </div>
  </div>

  <GenericDialog
    :visible="showDialog"
    title="Eliminar utilizador"
    description="Confirma que quer eliminar o utilizador?"
    @close="showDialog = false"
    @confirm="removeUser()"
  />

  <!-- Apply fixed table layout via inline style on DataTable -->
  <DataTable
    :rowHover="true"
    :value="users"
    :paginator="false"
    responsiveLayout="scroll"
    style="table-layout: fixed; width: 100%"
    @row-click="handleRowClick($event)"
    :rowClass="getRowClass"
    v-if="users.length"
  >
    <!-- Email Column with fixed width -->
    <Column
      field="email"
      :header="$t('views.accounts.table.email')"
      style="width: 300px"
    >
      <template #body="slotProps">
        <div class="text-cell email-cell" v-if="isEditingRow(slotProps.data)">
          <input v-model="editingUser.email" class="editable-field" />
        </div>
        <div class="text-cell email-cell" v-else>
          {{ slotProps.data.email }}
          <i
            v-if="slotProps.data.needsReactivation"
            class="pi pi-exclamation-triangle reactivation-warning-icon"
            title="Conta bloqueada, ncessário fazer a sua reativação."
          ></i>
        </div>
      </template>
    </Column>

    <!-- Phone Number Column with fixed width -->
    <Column
      field="phone"
      :header="$t('views.accounts.table.mobileNumber')"
      style="width: 150px"
    >
      <template #body="slotProps">
        <div class="text-cell phone-cell" v-if="isEditingRow(slotProps.data)">
          <input
            v-model="editingUser.phone"
            class="editable-field"
            placeholder="-"
          />
        </div>
        <div class="text-cell phone-cell" v-else>
          {{ slotProps.data.phone ? slotProps.data.phone : "-" }}
        </div>
      </template>
    </Column>

    <!-- Profile (Role) Column (no fixed width here, adjust as needed) -->
    <Column field="name" :header="$t('views.accounts.table.profile')">
      <template #body="slotProps">
        {{ $t("views.roles." + slotProps.data.role) }}
      </template>
    </Column>

    <!-- Action Buttons Column -->
    <Column bodyStyle="text-align: right">
      <template #body="slotProps">
        <div class="button-container">
          <!-- Non-editing buttons (edit & delete) -->
          <div
            v-show="!isEditingRow(slotProps.data)"
            class="non-editing-buttons"
          >
            <Button
              v-if="
                (slotProps.data.email != auth.email ||
                  slotProps.data.role != 'super_admin') &&
                auth.account_state == 'ENABLED'
              "
              icon="pi pi-pencil"
              class="p-button-rounded p-button-secondary p-button-text"
              @click="startEditUser(slotProps.data)"
            />
            <Button
              v-if="
                (slotProps.data.email != auth.email ||
                  slotProps.data.role != 'super_admin') &&
                auth.account_state == 'ENABLED'
              "
              icon="pi pi-trash"
              class="p-button-rounded p-button-secondary p-button-text"
              @click="(showDialog = true), (toDelete = slotProps.data.email)"
            />
          </div>
          <!-- Editing buttons (confirm & cancel) -->
          <div v-show="isEditingRow(slotProps.data)" class="editing-buttons">
            <span
              v-if="slotProps.data.needsReactivation"
              class="reactivate-account"
              @click="handleReactivateUser(slotProps.data)"
            >
              Reativar Conta
            </span>
            <Button
              icon="pi pi-check"
              class="p-button-rounded p-button-secondary p-button-text confirm-cancel-btn"
              @click="saveUser()"
            />
            <Button
              icon="pi pi-times"
              class="p-button-rounded p-button-secondary p-button-text confirm-cancel-btn"
              @click="cancelEdit()"
            />
          </div>
        </div>
      </template>
    </Column>
  </DataTable>

  <CustomPaginator
    :size="size"
    :totalRecords="Number(totalRecords)"
    :first="first"
    :rowsPerPageOptions="[5, 10, 20, 30]"
    @pageChange="handlePageChange"
    v-if="users.length"
  />
</template>

<script>
import {
  getUser,
  deleteUser,
  updateUser,
  activateUserAuth,
} from "@/services/user.service";
import EmptyState from "@/components/utils/EmptyState.vue";
import CustomPaginator from "@/components/CustomPaginator.vue";
import GenericDialog from "@/components/utils/GenericDialog.vue";
import AuthApp from "../../main";

export default {
  name: "applist",
  data: function () {
    return {
      auth: AuthApp,
      loading: true,
      first: 0,
      size: 10,
      page: 0,
      users: [],
      totalRecords: 0,
      toDelete: 0,
      showDialog: false,
      // For inline editing:
      editingUser: null, // A clone of the user being edited
      editingId: null, // Identifier (using the original email) for the row under edit
    };
  },
  mounted() {
    this.getData();
  },
  components: {
    EmptyState,
    CustomPaginator,
    GenericDialog,
  },
  methods: {
    handleRowClick(e) {
      console.log(JSON.stringify(e));
    },
    async getData(showLoader = true) {
      if (showLoader && !this.loader) {
        this.loading = true;
        this.loader = this.$loading.show();
      }

      const [res, err] = await getUser({ page: this.page, size: this.size });
      if (err) {
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Aconteceu um erro, por favor tente mais tarde!",
          life: 3000,
        });
        if (showLoader && this.loader) {
          this.loader.hide();
        }
      } else {
        this.users = res.items.map((user) => ({
          ...user,
          phone: user.mobileNumber,
          needsReactivation: !user.keycloakEnabled,
        }));

        this.totalRecords = res.totalItems;
      }
      if (showLoader && this.loader) {
        this.loading = false;
        this.loader.hide();
      }
    },
    async removeUser() {
      this.loading = true;
      this.loader = this.$loading.show();
      const [, err] = await deleteUser(this.toDelete);
      if (err) {
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Aconteceu um erro, por favor tente mais tarde!",
          life: 3000,
        });
        this.loader.hide();
      } else {
        this.users = this.users.filter((item) => item.email !== this.toDelete);
        this.toDelete = null;
        this.showDialog = false;
      }
      this.loading = false;
      this.loader.hide();
    },
    startEditUser(user) {
      // Clone the user and save the original email to track which row is being edited.
      this.editingUser = { ...user, originalEmail: user.email };
      this.editingId = user.email;
    },

    async handleReactivateUser(user) {
      this.loading = true;
      this.loader = this.$loading.show();

      const [, err] = await activateUserAuth(user.email);

      if (err) {
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Não foi possível reativar a conta!",
          life: 3000,
        });
      } else {
        // Optionally update the user's reactivation flag locally
        const index = this.users.findIndex((u) => u.id === user.id);
        if (index !== -1) {
          this.users[index] = { ...user, needsReactivation: false };
        }

        await this.getData(false);
        this.cancelEdit();
        this.$toast.add({
          severity: "success",
          summary: "Sucesso",
          detail: "Conta reativada com sucesso!",
          life: 3000,
        });
      }

      this.loading = false;
      this.loader.hide();
    },

    async saveUser() {
      this.loading = true;
      this.loader = this.$loading.show();
      const [, err] = await updateUser(this.editingUser);
      if (err) {
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Não foi possível atualizar o utilizador!",
          life: 3000,
        });
      } else {
        const index = this.users.findIndex(
          (item) => item.email === this.editingUser.originalEmail
        );
        if (index !== -1) {
          this.users[index] = { ...this.editingUser };
        }
        this.$toast.add({
          severity: "success",
          summary: "Sucesso",
          detail: "Utilizador atualizado com sucesso!",
          life: 3000,
        });
        this.cancelEdit();
      }
      this.loading = false;
      this.loader.hide();
    },
    cancelEdit() {
      this.editingUser = null;
      this.editingId = null;
    },
    isEditingRow(rowData) {
      return (
        this.editingUser && rowData.email === this.editingUser.originalEmail
      );
    },
    getRowClass(rowData) {
      return this.isEditingRow(rowData) ? "editing-row" : "";
    },
    handlePageChange(e) {
      this.page = e.page;
      this.size = e.rows;
      this.getData();
    },
  },
};
</script>

<style lang="css" scoped>
.p-datatable {
  margin-top: 10px !important;
}

/* Force table layout fixed so columns don’t resize unexpectedly */
.p-datatable-table {
  table-layout: fixed !important;
}

/* Highlight the row being edited */
.editing-row {
  background-color: #f0f8ff !important;
}

/* Common text-cell styling for both email and phone columns */
.text-cell {
  padding: 0.3rem;
  box-sizing: border-box;
  overflow: hidden;
  white-space: nowrap;
  text-align: left;
}

/* Force fixed width for email and phone cells */
.email-cell {
  width: 300px;
}
.phone-cell {
  width: 150px;
}

/* Styling for inline editable input fields */
.editable-field {
  width: 100%;
  padding: 0.3rem;
  box-sizing: border-box;
  border: 1px solid #ccc;
  border-radius: 3px;
}

/* Button container to prevent layout shifts */
.button-container {
  position: relative;
  min-width: 6rem;
  height: 2.5rem;
}

/* Position both groups of buttons absolutely so they overlap */
.non-editing-buttons,
.editing-buttons {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

/* Ensure confirm and cancel buttons have consistent, square size */
.confirm-cancel-btn {
  width: 2.5rem !important;
  height: 2.5rem !important;
  min-width: 2.5rem !important;
  min-height: 2.5rem !important;
  padding: 0 !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

.confirm-cancel-btn .pi {
  font-size: 1.2rem;
}

/* (Optional) Fade effect on visibility change */
.non-editing-buttons,
.editing-buttons {
  transition: opacity 0.3s;
}

.reactivate-account {
  font-size: 14px;
  color: gray;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.3s;
  margin-left: 0.5rem;
  align-self: center;
  padding-right: 1rem;
}
.reactivate-account:hover {
  color: #bad80a;
  opacity: 1;
  text-decoration: underline;
}

.reactivation-warning-icon {
  margin-left: 0.5rem; /* Space between the email text and the icon */
  color: red; /* Default color */
  transition: color 0.3s;
  vertical-align: middle; /* Aligns it with the email text */
}

.reactivation-warning-icon:hover {
  color: gray; /* Changes to green on hover */
}
</style>
